import { Component, inject, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ButtonModule, ButtonSeverity } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { Table, TableModule } from 'primeng/table';
import { CommonModule, DatePipe } from '@angular/common';
import { IUser, IUserCreateRequest, IUserEditRequest, UserStatus, UserStatusMeta } from '@app/shared/interfaces/user-definitions.interface';
import { BadgeModule } from 'primeng/badge';
import { DrawerModule } from 'primeng/drawer';
import { ToggleSwitch } from 'primeng/toggleswitch';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
    ValidatorFn,
    AbstractControl,
    ValidationErrors
} from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { UserDefinitionService } from '@services/user-definition.service';
import { MultiSelect } from 'primeng/multiselect';
import { filter } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-user-definitions',
    imports: [
        ButtonModule,
        InputText,
        FormsModule,
        CommonModule,
        ReactiveFormsModule,
        ToggleSwitch,
        DrawerModule,
        DialogModule,
        TableModule,
        MultiSelect,
        SelectModule,
        ColumnFilterComponent,
        IconField,
        InputIcon,
        PageHeaderComponent
    ],
    templateUrl: './user-definitions.component.html',
    styleUrl: './user-definitions.component.scss',
    providers: [DatePipe]
})
export class UserDefinitionsComponent implements OnInit {
    filterVisible: boolean = false;
    users: IUser[] = [];
    @ViewChild('dt') dt!: Table;
    userDisplayChecked: boolean = false;
    userStatusChecked: boolean = false;
    appliedUserDisplayChecked: boolean = false;
    appliedUserStatusChecked: boolean = false;
    userDialogVisible: boolean = false;
    selectedUser: any = null;
    isEditMode: boolean = false;
    userForm!: FormGroup;
    submitted: boolean = false;
    loading = false;
    showChangePasswordFields: boolean = false;
    statusList: string[] = [];
    agentList: { id: number; name: string }[] = [];
    roleList: { id: number; code: string; explation: string }[] = [];
    cols = [
        { field: 'username', header: 'Username' },
        { field: 'name', header: 'Name' },
        { field: 'email', header: 'Email' },
        { field: 'agent', header: 'Agent' },
        { field: 'status', header: 'Status' },
        { field: 'createdBy', header: 'Created By' },
        { field: 'createdDate', header: 'Created Date' },
        { field: 'modifiedBy', header: 'Modified By' },
        { field: 'modifiedDate', header: 'Modified Date' },
        { field: 'passwordExpiry', header: 'Password Expiry' }
    ];

    constructor(
        private datePipe: DatePipe,
        private fb: FormBuilder,
        private messageService: MessageService,
        private userDefinitionService: UserDefinitionService,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.getInitalData();
        this.initializeForm();
    }

    //Method to open filter drawer
    openFilter() {
        this.filterVisible = true;
    }

    onGlobalSearch(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.dt.filterGlobal(value, 'contains');
    }
    getInitalData() {
        this.loading = true;
        this.userDefinitionService.getUsers().subscribe((data) => {
            this.users = data;
            this.loading = false;
            this.cd.detectChanges();
        });
        this.userDefinitionService.getModalInformation().subscribe((data) => {
            this.statusList = data.statuses;
            this.agentList = data.agents;
            this.roleList = data.roles;
            this.cd.detectChanges();
        });
    }

    toggleShowChangePasswordFields() {
        this.showChangePasswordFields = !this.showChangePasswordFields;
        this.setMode(this.isEditMode);
    }
    //colorize status badge based on status
    private toUserStatus(statusStr: string): UserStatus | null {
        switch (statusStr.toLowerCase()) {
            case 'ok':
                return UserStatus.OK;
            case 'not_ok':
                return UserStatus.NOT_OK;
            case 'blocked':
                return UserStatus.BLOCKED;
            default:
                return null;
        }
    }
    friendlyName(statusStr: string): string {
        const st = this.toUserStatus(statusStr);
        return st ? UserStatusMeta[st].friendlyName : statusStr;
    }

    getStatusClass(statusStr: string): string {
        const st = this.toUserStatus(statusStr);
        if (!st) return '';
        return `status-badge status-${st}`;
    }

    //method to apply filter changes
    onToggleChanged() {
        this.userDefinitionService.getUsersByFilter(this.userStatusChecked, this.userDisplayChecked).subscribe((data) => {
            this.users = data;
            this.cd.detectChanges();
        });
        this.appliedUserDisplayChecked = this.userDisplayChecked;
        this.appliedUserStatusChecked = this.userStatusChecked;
        this.filterVisible = false;
    }

    cancelFilter() {
        this.userDisplayChecked = this.appliedUserDisplayChecked;
        this.userStatusChecked = this.appliedUserStatusChecked;
        this.filterVisible = false;
    }

    initializeForm(): void {
        this.userForm = this.fb.group({
            userName: ['', [Validators.required, Validators.pattern('^[A-Za-zğüşöçıİĞÜŞÖÇ]+$')]],
            name: ['', [Validators.required, Validators.pattern('^[A-Za-zğüşöçıİĞÜŞÖÇ\\s]+$')]],
            email: ['', [Validators.required, Validators.email]],
            password: [''],
            currentPassword: [''],
            newPassword: [''],
            retryNewPassword: [''],
            agent: ['', Validators.required],
            jobTitle: [''],
            workPhone: ['', Validators.pattern('^\\+?[0-9]+$')],
            role: [[]],
            department: [''],
            mobilePhone: ['', Validators.pattern('^\\+?[0-9]+$')],
            language: [''],
            status: ['']
        });
    }

    passwordValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value as string;
            if (!value) return null;

            const hasUpperCase = /[A-Z]/.test(value);
            const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
            const hasMinLength = value.length >= 8;

            return hasUpperCase && hasSpecialChar && hasMinLength ? null : { passwordStrength: true };
        };
    }

    isInvalid(field: string): boolean {
        const control = this.userForm.get(field);
        return !!(control && control.invalid && (this.submitted || control.dirty || control.touched));
    }
    openCreateUser() {
        this.userDialogVisible = true;
        this.resetForm();
        this.isEditMode = false;
        this.setMode(this.isEditMode);
        this.selectedUser = null;
        this.submitted = false;
    }

    setMode(isEdit: boolean) {
        const req = Validators.required;
        const strong = [req, Validators.minLength(8), this.passwordValidator()];
        if (!isEdit) {
            this.userForm.get('password')!.setValidators(strong);
            ['currentPassword', 'newPassword', 'retryNewPassword'].forEach((c) => {
                this.userForm.get(c)!.clearValidators();
                this.userForm.get(c)!.setValue('');
            });
        } else {
            this.userForm.get('password')!.clearValidators();
            this.userForm.get('password')!.setValue('');
            if (this.showChangePasswordFields) {
                this.userForm.get('currentPassword')!.setValidators(req);
                this.userForm.get('newPassword')!.setValidators(strong);
                this.userForm.get('retryNewPassword')!.setValidators(req);
            } else {
                ['currentPassword', 'newPassword', 'retryNewPassword'].forEach((c) => {
                    this.userForm.get(c)!.clearValidators();
                    this.userForm.get(c)!.setValue('');
                });
            }
        }

        ['password', 'currentPassword', 'newPassword', 'retryNewPassword'].forEach((c) =>
            this.userForm.get(c)!.updateValueAndValidity({ emitEvent: false })
        );
    }

    cancelUserFilter() {
        this.userDialogVisible = false;
        this.showChangePasswordFields = false;
        this.resetForm();
    }

    saveUser() {
        this.submitted = true;
        const formData = this.userForm.value;
        if (this.userForm.invalid) {
            return;
        }
        if (!this.isEditMode) {
            let request: IUserCreateRequest = {
                agentId: formData.agent,
                department: formData.department || '',
                email: formData.email,
                jobTitle: formData.jobTitle || '',
                userName: formData.userName,
                workPhone: formData.workPhone || '',
                mobilePhone: formData.mobilePhone || '',
                name: formData.name,
                language: formData.language || '',
                password: formData.password,
                roles: formData.role,
                status: formData.status || 'OK'
            };
            this.userDefinitionService.createUser(request).subscribe({
                next: (res) => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'The user has been created successfully.'
                    });
                    this.getInitalData();
                },
                error: (err) => {
                    console.error('Error creating user:', err);
                }
            });
        } else {
            const newPassword = this.userForm.get('newPassword')?.value;
            const retryNewPassword = this.userForm.get('retryNewPassword')?.value;

            if (newPassword !== retryNewPassword) {
                this.userForm.get('retryNewPassword')?.setErrors({ passwordMismatch: true });
                return;
            }
            let request: IUserEditRequest = {
                agentId: formData.agent,
                department: formData.department || '',
                email: formData.email,
                jobTitle: formData.jobTitle || '',
                userName: formData.userName,
                workPhone: formData.workPhone || '',
                mobilePhone: formData.mobilePhone || '',
                name: formData.name,
                roles: formData.role,
                language: formData.language || '',
                status: formData.status || 'ok',
                oldPassword: formData.currentPassword,
                newPassword: formData.newPassword,
                userId: this.selectedUser.userId
            };
            if (!this.showChangePasswordFields) {
                delete request.oldPassword;
                delete request.newPassword;
            }
            this.userDefinitionService.editUser(request).subscribe({
                next: (res) => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'The user has been updated successfully.'
                    });
                    this.getInitalData();
                }
            });
        }
        this.showChangePasswordFields = false;
        this.userDialogVisible = false;
        this.resetForm();
    }
    resetForm() {
        this.userForm.reset();
        this.submitted = false;
    }

    clearChanges() {
        if (this.isEditMode && this.selectedUser) {
            this.populateForm(this.selectedUser);
            this.userForm.markAsPristine();
            this.userForm.markAsUntouched();
            Object.keys(this.userForm.controls).forEach((key) => {
                this.userForm.get(key)?.setErrors(null);
            });
            this.submitted = false;
        } else {
            this.resetForm();
        }
    }
    openEditUser(user: any) {
        this.isEditMode = true;
        this.selectedUser = user;
        this.submitted = false;
        this.populateForm(user);
        this.setMode(this.isEditMode);
        this.userDialogVisible = true;
    }
    populateForm(user: any): void {
        this.userForm.patchValue({
            userName: user.userName,
            name: user.name,
            email: user.email,
            password: '',
            currentPassword: '',
            newPassword: '',
            retryNewPassword: '',
            agent: user.agent?.id,
            jobTitle: user.jobTitle || '',
            workPhone: user.workPhone || '',
            role: user.roles?.map((role: any) => role.id) || [],
            department: user.department || '',
            mobilePhone: user.mobilePhone || '',
            language: user.language || '',
            status: user.status || ''
        });
    }
}
